import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Plus, Copy, Check, Trash2, Link2, ExternalLink, X, Save, Loader2, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const empty = { slug: "", destination: "", label: "", utm_source: "", utm_medium: "", utm_campaign: "" };

export default function ShortLinksAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);

  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const siteOrigin = (typeof window !== "undefined") ? window.location.origin : BACKEND;

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/short-links")).data); }
    catch { toast.error("Impossible de charger les liens courts"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit.destination) return toast.error("URL de destination obligatoire");
    setSaving(true);
    try {
      if (edit.id) await api.put(`/short-links/${edit.id}`, edit);
      else await api.post("/short-links", edit);
      toast.success("Lien enregistré"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce lien court ?")) return;
    await api.delete(`/short-links/${id}`);
    toast.success("Supprimé"); load();
  };

  const copy = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(url); setTimeout(() => setCopied(null), 1500);
    toast.success("Lien copié");
  };

  const totalClicks = items.reduce((a, i) => a + (i.clicks || 0), 0);

  return (
    <div className="p-6 md:p-10" data-testid="admin-short-links-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-bold mb-2">Acquisition</div>
          <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">Liens courts trackés</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Créez des URLs courtes pour vos campagnes (flyers, SMS, QR code) et mesurez les clics en temps réel.</p>
        </div>
        <Button onClick={() => setEdit({ ...empty })} className="bg-[color:var(--brand-red)] font-bold" data-testid="new-short-link-btn">
          <Plus className="w-4 h-4 mr-1" />Nouveau lien
        </Button>
      </header>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card className="p-4 border">
          <Link2 className="w-4 h-4 text-[color:var(--brand-sky)] mb-2" />
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">Liens créés</div>
          <div className="font-display text-3xl font-black text-[color:var(--brand-navy)] tabular-nums">{items.length}</div>
        </Card>
        <Card className="p-4 border">
          <TrendingUp className="w-4 h-4 text-emerald-500 mb-2" />
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">Clics totaux</div>
          <div className="font-display text-3xl font-black text-[color:var(--brand-navy)] tabular-nums">{totalClicks}</div>
        </Card>
        <Card className="p-4 border">
          <ExternalLink className="w-4 h-4 text-amber-500 mb-2" />
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">Clics/lien (moy.)</div>
          <div className="font-display text-3xl font-black text-[color:var(--brand-navy)] tabular-nums">{items.length ? Math.round(totalClicks / items.length) : 0}</div>
        </Card>
      </div>

      {loading ? (
        <Card className="p-20 border flex items-center justify-center gap-2 text-sm text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</Card>
      ) : items.length === 0 ? (
        <Card className="p-16 border text-center">
          <Link2 className="w-8 h-8 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
          <div className="font-display font-bold text-[color:var(--brand-navy)]">Aucun lien court pour le moment</div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 mb-4">Créez votre premier lien pour tracker une campagne (flyer, SMS, affiche).</p>
          <Button onClick={() => setEdit({ ...empty })} className="bg-[color:var(--brand-red)]"><Plus className="w-4 h-4 mr-1" />Créer le premier</Button>
        </Card>
      ) : (
        <Card className="border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted))]/50 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Label</th>
                <th className="text-left p-3">Lien court</th>
                <th className="text-left p-3">Destination</th>
                <th className="text-left p-3">UTM</th>
                <th className="text-left p-3">Clics</th>
                <th className="text-left p-3">Dernier clic</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => {
                const shortUrl = `${siteOrigin}/l/${l.slug}`;
                return (
                  <tr key={l.id} className="border-t hover:bg-[hsl(var(--muted))]/20">
                    <td className="p-3 font-semibold max-w-xs truncate">{l.label}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <code className="text-xs bg-[hsl(var(--muted))] px-2 py-1 rounded font-mono truncate max-w-[280px]" title={shortUrl} data-testid={`short-url-${l.slug}`}>{shortUrl}</code>
                        <a href={shortUrl} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-md hover:bg-[hsl(var(--muted))] flex items-center justify-center shrink-0" title="Ouvrir" data-testid={`short-open-${l.slug}`}>
                          <ExternalLink className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                        </a>
                        <button onClick={() => copy(shortUrl)} className="w-7 h-7 rounded-md hover:bg-[hsl(var(--muted))] flex items-center justify-center shrink-0" title="Copier le lien complet" data-testid={`short-copy-${l.slug}`}>
                          {copied === shortUrl ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />}
                        </button>
                      </div>
                    </td>
                    <td className="p-3 max-w-[240px]">
                      <a href={l.destination} target="_blank" rel="noopener noreferrer" className="text-xs text-[color:var(--brand-sky)] hover:underline truncate flex items-center gap-1">
                        {l.destination} <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    </td>
                    <td className="p-3 text-[11px] font-mono text-[hsl(var(--muted-foreground))]">
                      {l.utm_source || "—"}{l.utm_campaign && <> · {l.utm_campaign}</>}
                    </td>
                    <td className="p-3">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[color:var(--brand-sky)]/15 text-[color:var(--brand-sky)] font-bold text-xs tabular-nums">
                        {l.clicks || 0}
                      </span>
                    </td>
                    <td className="p-3 text-[11px] font-mono text-[hsl(var(--muted-foreground))] whitespace-nowrap">
                      {l.last_click_at ? new Date(l.last_click_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => setEdit(l)} data-testid={`short-edit-${l.slug}`}><Save className="w-3 h-3" /></Button>
                        <Button variant="outline" size="sm" onClick={() => del(l.id)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h3 className="font-display font-bold text-xl text-[color:var(--brand-navy)]">{edit.id ? "Éditer" : "Créer un"} lien court</h3>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Slug court + URL de destination + UTM optionnels.</p>
              </div>
              <button onClick={() => setEdit(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label>URL de destination *</Label>
                <Input value={edit.destination} onChange={(e) => setEdit({ ...edit, destination: e.target.value })} placeholder="https://votre-site.fr/devis-gratuit" data-testid="short-dest" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Slug (auto-généré si vide)</Label>
                  <div className="flex items-center">
                    <span className="px-3 h-10 rounded-l-md border border-r-0 border-[hsl(var(--border))] bg-[hsl(var(--muted))] text-xs text-[hsl(var(--muted-foreground))] font-mono flex items-center">{siteOrigin}/l/</span>
                    <Input value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} placeholder="promo-ete" className="rounded-l-none" />
                  </div>
                </div>
                <div>
                  <Label>Label interne</Label>
                  <Input value={edit.label} onChange={(e) => setEdit({ ...edit, label: e.target.value })} placeholder="Flyer été 2026" />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-3 pt-2 border-t">
                <div><Label>UTM Source</Label><Input value={edit.utm_source || ""} onChange={(e) => setEdit({ ...edit, utm_source: e.target.value })} placeholder="flyer" /></div>
                <div><Label>UTM Medium</Label><Input value={edit.utm_medium || ""} onChange={(e) => setEdit({ ...edit, utm_medium: e.target.value })} placeholder="print" /></div>
                <div><Label>UTM Campaign</Label><Input value={edit.utm_campaign || ""} onChange={(e) => setEdit({ ...edit, utm_campaign: e.target.value })} placeholder="ete2026" /></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t">
              <Button variant="outline" onClick={() => setEdit(null)}>Annuler</Button>
              <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-navy)] font-bold" data-testid="short-save">
                {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />…</> : <><Save className="w-4 h-4 mr-1" />Enregistrer</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
